import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { consentApi } from '@/lib/api/consents';
import type {
  PartnerVisibilityNotice,
  PartnerVisibilityStatus,
  PartnerVisibilityStore,
} from '@/lib/api/types';

export interface UsePartnerVisibilityConsentsOptions {
  enabled: boolean;
  customerKey: number | string | null;
}

export interface UsePartnerVisibilityConsentsReturn {
  notice: PartnerVisibilityNotice | null;
  stores: PartnerVisibilityStore[];
  isInitialLoading: boolean;
  initialError: string | null;
  retry: () => void;
  nextCursor: string | null;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  loadMore: () => Promise<void>;
  pendingStoreIds: ReadonlySet<number>;
  mutationErrorByStoreId: ReadonlyMap<number, string>;
  update: (
    storeId: number,
    status: PartnerVisibilityStatus
  ) => Promise<void>;
}

interface ScopedState {
  scopeKey: string | null;
  notice: PartnerVisibilityNotice | null;
  stores: PartnerVisibilityStore[];
  isInitialLoading: boolean;
  initialError: string | null;
  nextCursor: string | null;
  isLoadingMore: boolean;
  loadMoreError: string | null;
}

interface MutationRequest {
  controller: AbortController;
  promise: Promise<void>;
}

const emptyScopedState = (
  scopeKey: string | null,
  isInitialLoading = false
): ScopedState => ({
  scopeKey,
  notice: null,
  stores: [],
  isInitialLoading,
  initialError: null,
  nextCursor: null,
  isLoadingMore: false,
  loadMoreError: null,
});

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

const noticesMatch = (
  left: PartnerVisibilityNotice | null,
  right: PartnerVisibilityNotice
): boolean =>
  left !== null &&
  left.version === right.version &&
  left.url === right.url;

const hasDuplicateStoreId = (
  existingStores: readonly PartnerVisibilityStore[],
  incomingStores: readonly PartnerVisibilityStore[]
): boolean => {
  const seen = new Set(existingStores.map((store) => store.store_id));

  for (const store of incomingStores) {
    if (seen.has(store.store_id)) {
      return true;
    }
    seen.add(store.store_id);
  }

  return false;
};

export function usePartnerVisibilityConsents({
  enabled,
  customerKey,
}: UsePartnerVisibilityConsentsOptions): UsePartnerVisibilityConsentsReturn {
  const scopeKey = useMemo(
    () =>
      enabled && customerKey !== null
        ? `${typeof customerKey}:${String(customerKey)}`
        : null,
    [customerKey, enabled]
  );
  const [state, setState] = useState<ScopedState>(() =>
    emptyScopedState(scopeKey, scopeKey !== null)
  );
  const [retryGeneration, setRetryGeneration] = useState(0);
  const [pendingStoreIds, setPendingStoreIds] = useState<ReadonlySet<number>>(
    () => new Set()
  );
  const [mutationErrorByStoreId, setMutationErrorByStoreId] = useState<
    ReadonlyMap<number, string>
  >(() => new Map());

  const stateRef = useRef(state);
  stateRef.current = state;
  const requestGenerationRef = useRef(0);
  const initialControllerRef = useRef<AbortController | null>(null);
  const loadMoreControllerRef = useRef<AbortController | null>(null);
  const mutationRequestsRef = useRef<Map<number, MutationRequest>>(new Map());

  const abortAll = useCallback(() => {
    initialControllerRef.current?.abort();
    initialControllerRef.current = null;
    loadMoreControllerRef.current?.abort();
    loadMoreControllerRef.current = null;

    for (const request of mutationRequestsRef.current.values()) {
      request.controller.abort();
    }
    mutationRequestsRef.current.clear();
  }, []);

  const restartFromFirstPage = useCallback(
    (targetScopeKey: string) => {
      requestGenerationRef.current += 1;
      abortAll();
      setState(emptyScopedState(targetScopeKey, true));
      setPendingStoreIds(new Set());
      setMutationErrorByStoreId(new Map());
      setRetryGeneration((current) => current + 1);
    },
    [abortAll]
  );

  useEffect(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    abortAll();
    setState(emptyScopedState(scopeKey, scopeKey !== null));
    setPendingStoreIds(new Set());
    setMutationErrorByStoreId(new Map());

    if (scopeKey === null) {
      return () => {
        if (requestGenerationRef.current === generation) {
          requestGenerationRef.current += 1;
        }
        abortAll();
      };
    }

    const controller = new AbortController();
    initialControllerRef.current = controller;

    void consentApi
      .getPartnerVisibility(undefined, controller.signal)
      .then((response) => {
        if (
          requestGenerationRef.current !== generation ||
          controller.signal.aborted
        ) {
          return;
        }

        setState({
          scopeKey,
          notice: response.data.notice,
          stores: response.data.stores,
          isInitialLoading: false,
          initialError: null,
          nextCursor: response.data.next_cursor,
          isLoadingMore: false,
          loadMoreError: null,
        });
      })
      .catch((error: unknown) => {
        if (
          requestGenerationRef.current !== generation ||
          controller.signal.aborted ||
          isAbortError(error)
        ) {
          return;
        }

        setState((previous) => ({
          ...previous,
          isInitialLoading: false,
          initialError: errorMessage(
            error,
            'No se pudo cargar la configuración de privacidad'
          ),
        }));
      })
      .finally(() => {
        if (initialControllerRef.current === controller) {
          initialControllerRef.current = null;
        }
      });

    return () => {
      if (requestGenerationRef.current === generation) {
        requestGenerationRef.current += 1;
      }
      abortAll();
    };
  }, [abortAll, retryGeneration, scopeKey]);

  const retry = useCallback(() => {
    if (scopeKey !== null) {
      restartFromFirstPage(scopeKey);
    }
  }, [restartFromFirstPage, scopeKey]);

  const loadMore = useCallback(async (): Promise<void> => {
    const currentState = stateRef.current;
    if (
      scopeKey === null ||
      currentState.scopeKey !== scopeKey ||
      currentState.nextCursor === null ||
      loadMoreControllerRef.current !== null
    ) {
      return;
    }

    const attemptedCursor = currentState.nextCursor;
    const generation = requestGenerationRef.current;
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    setState((previous) => ({
      ...previous,
      isLoadingMore: true,
      loadMoreError: null,
    }));

    try {
      const response = await consentApi.getPartnerVisibility(
        attemptedCursor,
        controller.signal
      );
      if (
        requestGenerationRef.current !== generation ||
        controller.signal.aborted
      ) {
        return;
      }

      const latestState = stateRef.current;
      if (!noticesMatch(latestState.notice, response.data.notice)) {
        restartFromFirstPage(scopeKey);
        return;
      }

      if (
        latestState.scopeKey !== scopeKey ||
        hasDuplicateStoreId(latestState.stores, response.data.stores)
      ) {
        throw new Error(
          'The server returned a duplicate Store; retry this page.'
        );
      }

      setState((previous) => ({
        ...previous,
        notice: response.data.notice,
        stores: [...previous.stores, ...response.data.stores],
        nextCursor: response.data.next_cursor,
        isLoadingMore: false,
        loadMoreError: null,
      }));
    } catch (error: unknown) {
      if (
        requestGenerationRef.current !== generation ||
        controller.signal.aborted ||
        isAbortError(error)
      ) {
        return;
      }

      setState((previous) => ({
        ...previous,
        isLoadingMore: false,
        loadMoreError: errorMessage(
          error,
          'No se pudieron cargar más establecimientos'
        ),
      }));
    } finally {
      if (loadMoreControllerRef.current === controller) {
        loadMoreControllerRef.current = null;
      }
    }
  }, [restartFromFirstPage, scopeKey]);

  const update = useCallback(
    (
      storeId: number,
      status: PartnerVisibilityStatus
    ): Promise<void> => {
      const currentRequest = mutationRequestsRef.current.get(storeId);
      if (currentRequest !== undefined) {
        return currentRequest.promise;
      }

      const currentState = stateRef.current;
      if (scopeKey === null || currentState.scopeKey !== scopeKey) {
        return Promise.resolve();
      }

      const generation = requestGenerationRef.current;
      const controller = new AbortController();
      setPendingStoreIds((previous) => new Set(previous).add(storeId));
      setMutationErrorByStoreId((previous) => {
        const next = new Map(previous);
        next.delete(storeId);
        return next;
      });

      const operation = (async () => {
        try {
          const response = await consentApi.recordPartnerVisibility(
            { store_id: storeId, status, via: 'settings' },
            controller.signal
          );
          if (
            requestGenerationRef.current !== generation ||
            controller.signal.aborted
          ) {
            return;
          }

          if (
            !noticesMatch(
              stateRef.current.notice,
              response.data.notice
            )
          ) {
            restartFromFirstPage(scopeKey);
            return;
          }

          setState((previous) => ({
            ...previous,
            notice: response.data.notice,
            stores: previous.stores.map((store) =>
              store.store_id === storeId ? response.data.store : store
            ),
          }));
        } catch (error: unknown) {
          if (
            requestGenerationRef.current !== generation ||
            controller.signal.aborted ||
            isAbortError(error)
          ) {
            return;
          }

          setMutationErrorByStoreId((previous) => {
            const next = new Map(previous);
            next.set(
              storeId,
              errorMessage(
                error,
                'No se pudo actualizar la configuración de privacidad'
              )
            );
            return next;
          });
        } finally {
          const activeRequest = mutationRequestsRef.current.get(storeId);
          if (activeRequest?.controller === controller) {
            mutationRequestsRef.current.delete(storeId);
          }

          if (requestGenerationRef.current === generation) {
            setPendingStoreIds((previous) => {
              const next = new Set(previous);
              next.delete(storeId);
              return next;
            });
          }
        }
      })();

      mutationRequestsRef.current.set(storeId, {
        controller,
        promise: operation,
      });

      return operation;
    },
    [restartFromFirstPage, scopeKey]
  );

  const stateMatchesScope = state.scopeKey === scopeKey;
  const visibleState = stateMatchesScope
    ? state
    : emptyScopedState(scopeKey, scopeKey !== null);

  return {
    notice: visibleState.notice,
    stores: visibleState.stores,
    isInitialLoading: visibleState.isInitialLoading,
    initialError: visibleState.initialError,
    retry,
    nextCursor: visibleState.nextCursor,
    isLoadingMore: visibleState.isLoadingMore,
    loadMoreError: visibleState.loadMoreError,
    loadMore,
    pendingStoreIds: stateMatchesScope ? pendingStoreIds : new Set(),
    mutationErrorByStoreId: stateMatchesScope
      ? mutationErrorByStoreId
      : new Map(),
    update,
  };
}
